import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useApp } from '@/store/useApp';

/**
 * Manual HTML/CSS editor. The chat-or-nothing limit was a real ceiling for power
 * users — sometimes the fastest fix is `padding: 1rem 2rem` instead of asking the
 * model to do it. Save persists the edit as a new version (no model round-trip).
 *
 * Implementation notes:
 *   - We use a styled `<textarea>` rather than CodeMirror to keep the bundle slim
 *     (~70 KB saved). The trade-off is no syntax highlighting; designers comfortable
 *     enough to drop into Code mode are usually OK with that.
 *   - The current artifact is loaded into the textarea on mount. Editing is local
 *     until the user hits Save — this is intentional so accidental keystrokes don't
 *     mint dozens of versions.
 *   - Tab inserts two spaces rather than escaping focus.
 */
export function CodePanel() {
  const artifactHtml = useApp((s) => s.artifactHtml);
  const saveCodeEdit = useApp((s) => s.saveCodeEdit);
  const setMode = useApp((s) => s.setMode);

  const [draft, setDraft] = useState(artifactHtml);
  const [busy, setBusy] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  // Reload the draft whenever the underlying artifact changes (e.g. you make an
  // edit elsewhere and come back to Code mode). The dirty-buffer warning below
  // keeps unsaved local changes from being clobbered silently.
  const [hydratedFor, setHydratedFor] = useState(artifactHtml);
  useEffect(() => {
    if (artifactHtml !== hydratedFor && draft === hydratedFor) {
      setDraft(artifactHtml);
      setHydratedFor(artifactHtml);
    } else if (artifactHtml !== hydratedFor) {
      // Local edits exist; warn before losing them.
      const ok = window.confirm(
        'The artifact changed elsewhere (chat edit?). Discard your unsaved Code-mode changes and reload?',
      );
      if (ok) {
        setDraft(artifactHtml);
        setHydratedFor(artifactHtml);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactHtml]);

  const dirty = draft !== artifactHtml;

  async function handleSave() {
    if (!dirty || !draft.trim()) return;
    setBusy(true);
    try {
      await saveCodeEdit(draft);
      setHydratedFor(draft);
    } finally {
      setBusy(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Tab = two spaces, not focus shift. Standard code-editor behaviour.
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.currentTarget;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = ta.value.slice(0, start) + '  ' + ta.value.slice(end);
      setDraft(next);
      // Restore caret on next paint.
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2;
      });
      return;
    }
    // ⌘S / Ctrl+S = Save without leaving Code mode.
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      void handleSave();
    }
  }

  const lineCount = draft.split('\n').length;
  const charCount = draft.length;

  return (
    <aside className="flex h-full w-[480px] shrink-0 flex-col border-l border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-neutral-200 px-3 py-2 dark:border-neutral-800">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Code</h2>
          <span className="font-mono text-[11px] text-neutral-400">
            {lineCount} lines · {(charCount / 1024).toFixed(1)} KB
          </span>
          {dirty && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              unsaved
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setMode('select')}>
            Close
          </Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={!dirty || busy}>
            {busy ? 'Saving…' : 'Save as new version'}
          </Button>
        </div>
      </div>
      <textarea
        ref={taRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        spellCheck={false}
        autoComplete="off"
        className="flex-1 resize-none border-0 bg-neutral-50 p-3 font-mono text-[12px] leading-relaxed text-neutral-800 outline-none focus-visible:bg-white dark:bg-neutral-950 dark:text-neutral-200 dark:focus-visible:bg-neutral-900"
        aria-label="HTML source editor"
        placeholder="Paste or edit HTML here. Save creates a new version."
      />
      <p className="shrink-0 border-t border-neutral-200 px-3 py-1.5 text-[11px] text-neutral-500 dark:border-neutral-800">
        Tab = 2 spaces · ⌘S to save · Save mints a new version with this exact HTML.
      </p>
    </aside>
  );
}
